import { Bodenform, HumusAuflage } from '../data-model';
import { auflageGesamt } from './detail/hl-area/humus-auflage';

/**
 * Filz-Bodenformen: Bei ihnen gehoeren die Auflagehorizonte Ol, Of und Oh zum
 * Profil. Die Gelaendeoberflaeche (0 cm) liegt an der OBERKANTE des Ol, nicht
 * wie sonst an der Unterkante der Auflage (Nutzer-Vorgabe 24.09.2026). Das
 * aendert allein die Darstellung in der Boden-Uebersicht - gespeichert wird
 * nichts anders, und die Eingabemaske bleibt, wie sie ist.
 *
 * Zwei Wege fuehren dorthin, einer reicht (`istFilzProfil`):
 * 1. Die gewaehlte Bodenform ist eine Filz-Bodenform (`istFilzBodenform`).
 * 2. Die eingegebene Auflage ist maechtiger als 11 cm (`hatFilzAuflage`) -
 *    Filze definieren sich ueber die O-Maechtigkeit (Nutzer 24.09.2026), die
 *    Ansicht schaltet also schon mit der Eingabe um, bevor eine Bodenform
 *    gewaehlt ist.
 *
 * Erkannt wird eine Filz-Bodenform am KLARTEXT des Katalogs, nicht am Kuerzel:
 * Im HBF-Kuerzel steht F sowohl fuer Filz („s FRP" = Sand-Filzrostpodsol) als
 * auch fuer Fahlerde („s/l F", „l FaÜP") und Fen („im Fe"). Der Klartext der
 * Hauptbodenform allein reicht aber auch nicht: Fuer Doppel- und Dreifachfilz
 * fuehrt der Hauptbodenformen-Katalog teils noch die alte Bezeichnung
 * („Sand-Moorgleyhumuspodsol" fuer „s GFFHP"), waehrend die Feinbodenformen
 * darunter laengst „…Gleydoppelfilzhumuspodsol" heissen. Deshalb zaehlt eine
 * Hauptbodenform als Filz, sobald ihr eigener Klartext ODER der Klartext einer
 * ihrer Feinbodenformen „filz" enthaelt.
 *
 * Quelle ist die Feinbodenformen-Liste, wie die App sie fuehrt
 * (`getHardcoded('Feinbodenformen')`: Katalog aus der Datenbank plus eigene
 * Zusaetze) - nicht die gebuendelte JSON-Datei.
 */
export interface FilzKatalogZeile {
	HBF?: string;
	FBF?: string;
	/** Lokalname, z.B. „Albersdorfer" - Rueckfall, wenn `name_lang` fehlt. */
	name?: string;
	/** Volle Bezeichnung, z.B. „Albersdorfer Lehmsand-Staugleyhumusrostpodsol". */
	name_lang?: string;
	/** Klartext der Hauptbodenform, z.B. „Lehmsand-Staugleyhumusrostpodsol". */
	HBF_lang?: string;
}

/** Die Kuerzel, die Filz fuehren - einmal aus der Liste gebaut. */
export interface FilzNachschlag {
	hbf: Set<string>;
	fbf: Set<string>;
}

const FILZ = /filz/i;

export function filzNachschlag(zeilen: FilzKatalogZeile[] | null | undefined): FilzNachschlag {
	const hbf = new Set<string>();
	const fbf = new Set<string>();
	for (const z of zeilen ?? []) {
		const hbfCode = (z?.HBF ?? '').trim();
		const fbfCode = (z?.FBF ?? '').trim();
		const fbfFilz = FILZ.test(z?.name_lang || z?.name || '');
		if (fbfCode && fbfFilz) fbf.add(fbfCode);
		if (hbfCode && (fbfFilz || FILZ.test(z?.HBF_lang ?? ''))) hbf.add(hbfCode);
	}
	return { hbf, fbf };
}

/**
 * Ist die Bodenform des Punktes eine Filz-Bodenform?
 *
 * Entscheidend ist das OBERSTE Glied: Bei einem begrabenen Boden liegt die
 * Auflage auf dem oberen Boden; der begrabene darunter hat mit ihr nichts zu
 * tun. Gefragt werden Haupt- UND Feinbodenform des Gliedes - eine reicht.
 * Ohne Bodenform, ohne Liste und bei unbekannten Kuerzeln: keine Filz-Form.
 * Lieber die gewohnte Darstellung als eine geratene.
 */
export function istFilzBodenform(bodenform: Bodenform | null | undefined, nachschlag: FilzNachschlag): boolean {
	const oberstes = (bodenform?.bf ?? []).find(p => !!(p?.hbf || p?.fbf));
	if (!oberstes) return false;
	const hbf = (oberstes.hbf ?? '').trim();
	const fbf = (oberstes.fbf ?? '').trim();
	return (!!hbf && nachschlag.hbf.has(hbf)) || (!!fbf && nachschlag.fbf.has(fbf));
}

/**
 * Ab welcher Auflage ein Filz vorliegt: MEHR als 11 cm (Nutzer 24.09.2026).
 * Bei ganzen Zentimetern heisst das ab 12 cm - der Steckbrief des
 * Sand-Filzrostpodsols nennt „O (> 12 … 20 cm)".
 */
export const FILZ_AUFLAGE_MEHR_ALS_CM = 11;

/** Ist die eingegebene Auflage (Ol + Of + Oh) maechtig genug fuer einen Filz? */
export function hatFilzAuflage(auflage: HumusAuflage | null | undefined): boolean {
	return auflageGesamt(auflage) > FILZ_AUFLAGE_MEHR_ALS_CM;
}

/**
 * Gehoert die Auflage dieses Punktes zum Profil? Ja, wenn die Auflage einen
 * Filz ergibt ODER die Bodenform eine Filz-Bodenform ist. Die Liste darf
 * fehlen (beim ersten Blick ist sie nicht immer schon da) - dann zaehlt allein
 * die Auflage.
 */
export function istFilzProfil(
	bodenform: Bodenform | null | undefined,
	auflage: HumusAuflage | null | undefined,
	nachschlag?: FilzNachschlag,
): boolean {
	return hatFilzAuflage(auflage) || (!!nachschlag && istFilzBodenform(bodenform, nachschlag));
}
